import { useRef, useState, useLayoutEffect, useEffect } from 'react';
import hero720Webm from '../assets/home_page_assets/hero/hero_720_webm.webm';
import hero720Mp4 from '../assets/home_page_assets/hero/hero_720_mp4.mp4';
import hero450Webm from '../assets/home_page_assets/hero/hero_450_webm.webm';
import hero450Mp4 from '../assets/home_page_assets/hero/hero_450_mp4.mp4';
import heroPoster from '../assets/home_page_assets/hero/poster.webp';

export { heroPoster };

function useMobileVideo() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 767px)').matches
      : false
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');

    const handleChange = (event) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  return isMobile;
}

function shouldShowPosterOnly() {
  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData =
    typeof navigator !== 'undefined' &&
    navigator.connection?.saveData === true;
  return reducedMotion || saveData;
}

function HeroVideo() {
  const videoRef = useRef(null);
  const isMobile = useMobileVideo();
  const [showPosterOnly, setShowPosterOnly] = useState(() => {
    if (typeof window === 'undefined') return false;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData =
      typeof navigator !== 'undefined' &&
      navigator.connection?.saveData === true;
    return reducedMotion || saveData;
  });
  const [videoFailed, setVideoFailed] = useState(false);

  const webmSource = isMobile ? hero450Webm : hero720Webm;
  const mp4Source = isMobile ? hero450Mp4 : hero720Mp4;

  useLayoutEffect(() => {
    setShowPosterOnly(shouldShowPosterOnly());

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleMotionChange = (event) => {
      if (event.matches) {
        setShowPosterOnly(true);
      } else {
        const saveData =
          typeof navigator !== 'undefined' &&
          navigator.connection?.saveData === true;
        setShowPosterOnly(saveData);
      }
    };

    if (motionQuery.addEventListener) {
      motionQuery.addEventListener('change', handleMotionChange);
    } else if (motionQuery.addListener) {
      motionQuery.addListener(handleMotionChange);
    }

    return () => {
      if (motionQuery.removeEventListener) {
        motionQuery.removeEventListener('change', handleMotionChange);
      } else if (motionQuery.removeListener) {
        motionQuery.removeListener(handleMotionChange);
      }
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || showPosterOnly) return undefined;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // Keep the poster visible if autoplay is blocked.
      });
    }

    const handleVisibilityChange = () => {
      if (shouldShowPosterOnly()) return;
      if (document.visibilityState === 'hidden') {
        video.pause();
      } else {
        video.play().catch(() => {});
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [webmSource, mp4Source, showPosterOnly]);

  const showPoster = showPosterOnly || videoFailed;

  return (
    <div
      className="hero-video-viewport"
      style={{ backgroundImage: `url(${heroPoster})` }}
    >
      {showPoster ? (
        <img
          src={heroPoster}
          className="hero-video-poster"
          alt=""
          aria-hidden="true"
        />
      ) : (
        <video
          key={`${isMobile ? 'mobile' : 'desktop'}-hero-video`}
          ref={videoRef}
          className="hero-video"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={heroPoster}
          aria-hidden="true"
          tabIndex={-1}
          disablePictureInPicture
          onError={() => setVideoFailed(true)}
          onCanPlay={() => {
            setVideoFailed(false);
            videoRef.current?.play().catch(() => {});
          }}
        >
          <source src={webmSource} type="video/webm" />
          <source src={mp4Source} type="video/mp4" />
        </video>
      )}
    </div>
  );
}

export default HeroVideo;
